/* eslint-disable max-lines */
import { getEventBusIfDefined } from '@cian/event-bus';
import {
  createContext as createAuthenticationContext,
  EEventType,
  EActiveTab,
  EActiveView,
  ESentEmailType,
} from '@cian/authentication-widget';

import { httpApi, logger, config } from './tools';

import { Authenticator } from './utils/auth';
import { getCookie, setCookie } from './utils/cookie';
import { dropdownUrlChangeHandler } from './dropdown_menu/links';
import { waitForRegistrationPopupFactory, waitForLoginPopupFactory } from './utils/authentication';
import { EAlertType } from './AuthenticationPopup';
import * as loginMRO from './utils/loginMRO';
import { getNewbuildingSubdomain } from './utils/subdomain';
import { closeAuthenticationPopup } from './services/authentication';
import { getGetHeaderMarkup, IGetHeaderResponse200 } from '../shared/repositories/header/v1/get-header';
import { IHeaderV1Schema } from '../shared/repositories/header/entities/response/HeaderV1Schema';
import { toggleSettings } from './utils/toggleSettings';
import { initializeChatsNotificator, initializeNewChatsNotificator } from './services/chats';
import { getConfig } from '@cian/config/browser';
import { mapSectionToCategory } from '../shared/mappers/mapSectionToCategory';
import { ca } from '@cian/analytics';
import { initializeNotificationCenter } from './services/notificationCenter';
import { initializeUserMenu } from './services/userMenu';

export const removeRedwareCookies = async () => {
  if (config.get<boolean>('browser.removeRedwareCookieEnabled')) {
    // Хер знает число ли этот шанс..
    let chanceToStartThisShit = Number(config.get<number>('removeRedwareCookies.cleanChance'));
    if (isNaN(chanceToStartThisShit)) {
      chanceToStartThisShit = 0;
    }
    if (Math.random() >= chanceToStartThisShit) {
      return;
    }

    const trottlingTimeout = config.get<number>('removeRedwareCookies.requestTimeout') || 0;
    const currentHostIndex = getCookie('rdwr_i');
    const startIndex = currentHostIndex !== '' ? Number(currentHostIndex) : 0;

    let hostList = config.get<string[]>('removeRedwareCookies.hostList') || [];

    let keepGoing = !!currentHostIndex;
    if (!currentHostIndex) {
      let hostListToTry = [...hostList];
      hostListToTry.sort(() => Math.random() - 0.5);
      hostListToTry = hostListToTry.slice(0, 5);
      hostListToTry = ['my.cian.ru', 'api.cian.ru', 'www.cian.ru', ...hostListToTry];

      for (let i = 0; i < hostListToTry.length; i++) {
        const host = hostListToTry[i];

        try {
          const response = await fetchWithRetry(`https://${host}/kill-cookies/?host=${decodeURIComponent(host)}`, {
            credentials: 'include',
          });

          if (response.ok) {
            const responseJson = await response.json();

            if (responseJson.cookiesDeleted) {
              keepGoing = true;
              break;
            } else {
              if (i === hostListToTry.length - 1) {
                setCookie('rdwr_i', String(hostList.length - 1), 7);
              }
            }
          }

          await sleep(trottlingTimeout);
        } catch (e) {
          logger.error(e);
          break;
        }
      }
    }

    if (!keepGoing || startIndex === hostList.length - 1 || startIndex > hostList.length) {
      return;
    }

    for (let i = startIndex; i < hostList.length; i++) {
      const host = hostList[i];

      try {
        await fetchWithRetry(`https://${host}/kill-cookies/?host=${decodeURIComponent(host)}`, {
          credentials: 'include',
        });
        setCookie('rdwr_i', String(i), 7);

        await sleep(trottlingTimeout);
      } catch (e) {
        logger.error(e);
        break;
      }
    }
  }
};

const RETRY_COUNT = 3;

async function fetchWithRetry(url: RequestInfo, options?: RequestInit) {
  let tryCount = 0;
  let error: unknown;

  while (tryCount < RETRY_COUNT) {
    try {
      return await fetch(url, options);
    } catch (e) {
      tryCount++;
      error = e;
    }
  }

  throw error;
}

async function sleep(timeout: number = 0) {
  return new Promise(resolve => setTimeout(resolve, timeout));
}

/* Authentication Popup */
const isUserAuthenticated = !!window.__header_user_is_authenticated__;

const subdomain = config.getStrict<string>('subdomain');
const newbuildingSubdomain = getNewbuildingSubdomain(window.location.hostname, subdomain);
const linksBaseUrl = config.getStrict<string>('legacy.linksBaseUrl');

// Подсовываем в сабдомен виджета авторизации сабдомен ЖК, чтобы запросы не отваливались по CORS
// В других местах для правильной отрисовки и правильных переходов в хедере нужен именно гео сабдомен
// Техдолг будем решать после раскатки сабдоменов ЖК из эксперимента
const authenticationContext = createAuthenticationContext({
  httpApi,
  logger,
  subdomain: newbuildingSubdomain || subdomain,
});

const defaultAuthenticationSucceedCallback = () => {
  window.location.reload();
};

const handleRegisterSucceed = ({ isSms }: { isSms: boolean }) => {
  if (isSms) {
    defaultAuthenticationSucceedCallback();
  }
};

const handleSocialAuthenticateSucceed = ({ isNewUser }: { isNewUser: boolean }) => {
  if (!isNewUser) {
    defaultAuthenticationSucceedCallback();
  }
};

const handleEmailSentClosed = ({ type }: { type: ESentEmailType }) => {
  if (type === ESentEmailType.Registration) {
    defaultAuthenticationSucceedCallback();
  }
};

authenticationContext.on(EEventType.AuthenticateSucceed, defaultAuthenticationSucceedCallback);
authenticationContext.on(EEventType.RegisterSucceed, handleRegisterSucceed);
authenticationContext.on(EEventType.SocialAuthenticateSucceed, handleSocialAuthenticateSucceed);
authenticationContext.on(EEventType.EmailSentClosed, handleEmailSentClosed);

const offDefaultCallback = () => {
  authenticationContext.off(EEventType.AuthenticateSucceed, defaultAuthenticationSucceedCallback);
  authenticationContext.off(EEventType.RegisterSucceed, handleRegisterSucceed);
  authenticationContext.off(EEventType.SocialAuthenticateSucceed, handleSocialAuthenticateSucceed);
  authenticationContext.off(EEventType.EmailSentClosed, handleEmailSentClosed);
};

export const waitForLoginPopup: (alert?: EAlertType, onSuccess?: () => void) => void = (alert, onSuccess) => {
  offDefaultCallback();

  authenticationContext.resetAuthentication();
  authenticationContext.setTab(EActiveTab.Authentication);
  authenticationContext.setView(EActiveView.AuthenticationRegistration);

  waitForLoginPopupFactory({
    authenticationContext,
    defaultCallback: defaultAuthenticationSucceedCallback,
  })(alert, onSuccess);
};

export const waitForRegistrationPopup: () => void = () => {
  offDefaultCallback();

  waitForRegistrationPopupFactory({
    authenticationContext,
    defaultCallback: defaultAuthenticationSucceedCallback,
  })();
};

authenticationContext.on(EEventType.MROAuthUnavailable, () => {
  loginMRO.invalidate();
});

authenticationContext.on(EEventType.MROAuthActionRequired, () => {
  waitForLoginPopupFactory({
    authenticationContext,
    defaultCallback: defaultAuthenticationSucceedCallback,
  })();
});

if (loginMRO.isAvailable() && !isUserAuthenticated && getConfig().get('section') !== 'authentication') {
  authenticationContext
    .showMROAuthOneTap()
    .then(() => {
      loginMRO.invalidate();
    })
    .catch(error => console.error(error));
}
/* /Authentication Popup */

const logoutAction = () => {
  const { protocol, hostname } = window.location;

  const isLK = /(^|\.)(customer|my)\./i.test(hostname);

  if (isLK) {
    window.location.replace(`${protocol}//${hostname.replace(/(^|\.)(customer|my)\./i, '$1www.')}`);
  } else {
    window.location.reload();
  }
};

const logoutStart = (button: HTMLElement) => {
  window.__header_logout = button.onclick = (event: MouseEvent | React.MouseEvent<HTMLElement>) => {
    event.preventDefault();

    submitGA('logout');

    Authenticator.logOut(logoutAction, logoutAction, window.__header_logOut_base_url__);
  };
};

/* Logout */
{
  const logoutBtn = document.getElementById('logout-btn');
  const logoutBtnMobile = document.getElementById('logout-btn_mobile');

  if (logoutBtn) {
    logoutStart(logoutBtn);
  }

  if (logoutBtnMobile) {
    logoutStart(logoutBtnMobile);
  }
}
/* /Logout */

export const toggle = (element: HTMLElement | null, show: boolean, data?: string | number) => {
  if (element) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    element.style.display = show ? (null as any) : 'none';

    if (
      data !== undefined &&
      data !== null &&
      element.firstElementChild &&
      element.firstElementChild.firstElementChild
    ) {
      const el = element.firstElementChild.firstElementChild as HTMLElement;
      el.setAttribute('data-count', data.toString());
    }
  }
};

const getCounter = (counter?: number | string): number | string | undefined =>
  counter && Number(counter) > 99 ? '99+' : counter;

export const toggleWithAddClass = (
  element: HTMLElement | null,
  show: boolean,
  className: string | null,
  data?: string | number,
) => {
  if (element) {
    toggle(element, show, data);

    const count = Number(data);
    const span = element.querySelector('.c-header-messages--adaptive span') as HTMLElement;
    const notificationSpan = element.querySelector('.c-header-active-notification') as HTMLElement;

    if (count > 0 && notificationSpan) {
      notificationSpan.style.display = 'block';
    } else if (notificationSpan) {
      notificationSpan.style.display = 'none';
    }

    if (!className || !span) {
      return;
    }

    if (count > 0) {
      span.classList.add(className);
    } else {
      span.classList.remove(className);
    }
  }
};

const updateMobileFavoritesCounter = (element: HTMLElement | null, data: string | number) => {
  if (!element) {
    return;
  }

  toggle(element, true, data);
  const count = Number(data);
  const notificationSpan = element.querySelector('.c-header-active-notification') as HTMLElement | null;

  if (notificationSpan) {
    if (!isNaN(count) && count > 0) {
      notificationSpan.style.display = 'block';
    } else {
      notificationSpan.style.display = 'none';
    }
  }
};

const setHref = (element: HTMLElement | null, data: string) => {
  if (element) {
    element.setAttribute('href', data);
  }
};

const updateUserInfo = (user: IHeaderV1Schema) => {
  const newHeaderEnabled = !!getConfig().get<boolean>('newHeaderEnabled');

  const userSearch = toggleSettings(newHeaderEnabled, 'header-user-searches');
  const userFavorites = toggleSettings(newHeaderEnabled, 'header-user-favourites');

  toggleWithAddClass(
    userSearch.block,
    user.isAuthenticated,
    userSearch.counterClassName,
    user.storedFiltersCount || '',
  );
  toggleWithAddClass(userFavorites.block, true, userFavorites.counterClassName, user.favoritesCount || '');
  toggle(document.getElementById('header-user-balance'), user.isAuthenticated, user.balance + ' ₽');
  toggle(document.getElementById('header-user-name'), user.isAuthenticated, user.displayUsername || undefined);
  toggle(document.getElementById('header-user-calls'), Boolean(user.showCallsStatisticsLink));
  toggle(document.getElementById('header-user-personal'), user.isAuthenticated);
  toggle(document.getElementById('header-user-logout'), user.isAuthenticated);
  toggle(document.getElementById('header-user-login'), !user.isAuthenticated);
  setHref(document.getElementById('header-user-balance-link'), user.lkBalanceUrl || '');
  setHref(document.getElementById('header-user-name-link'), user.lkUrl || '');
  setHref(document.getElementById('header-user-personal-link'), user.lkUrl || '');
  if (user.email) {
    setHref(
      document.getElementById('header-user-calls-link'),
      `/user/cian/Statistics/ReportByCalls.aspx?userEmail=${user.email}`,
    );
  }
};

/* istanbul ignore next */
const updateMobileUserInfo = (user: IHeaderV1Schema) => {
  toggle(document.getElementById('header-chats-icon'), user.isAuthenticated);
  toggle(document.querySelector('.c-header__notifications-mobile-wrap'), user.isAuthenticated);

  const authInfo = document.getElementById('c-header__auth-block');

  if (authInfo && user.isAuthenticated && user.displayUsername) {
    authInfo.setAttribute('href', user.lkUrl || '');
    authInfo.onclick = () => {
      if (window._headerTrackingEvent) {
        window._headerTrackingEvent('main');
      }
    };
    authInfo.innerHTML = `
      <span class="c-header__main-nav-item_user-lk_name" id="adaptiveHeaderMenuUserName">${user.displayUsername}</span>
      <span class="c-header__main-nav-item_user-lk_label">Личный кабинет</span>
    `;
  }

  const logout = document.getElementById('c-header__mobile-logout');
  if (logout && user.isAuthenticated) {
    logout.innerHTML = `
      <div class="c-header__main-nav-logout">
        <div class="c-header__main-nav-divider"></div>
          <div class="c-header__main-nav_logout-wrapper">
            <button type="button" class="c-header__main-nav_logout" id="logout-btn_mobile">
              <span class="c-header__main-nav-item_logout-icon"></span>
              Выйти
            </button>
          </div>
        <div class="c-header__main-nav-divider"></div>
      </div>
    `;

    const logoutBtnMobile = document.getElementById('logout-btn_mobile');

    if (logoutBtnMobile) {
      logoutStart(logoutBtnMobile);
    }
  }

  const userDisplayed = document.getElementById('adaptiveHeaderUserName');
  if (userDisplayed && user.displayUsername) {
    userDisplayed.innerHTML = user.displayUsername;
  }

  const searchLink = document.querySelector('.c-header__searched-mobile-wrap a');

  if (searchLink) {
    const notificationSpan = searchLink.querySelector('.c-header-active-notification') as HTMLElement;

    if (user.storedFiltersCount) {
      notificationSpan.style.display = 'block';
    } else {
      notificationSpan.style.display = 'none';
    }

    toggle(
      (searchLink as HTMLElement).parentNode as HTMLElement,
      user.isAuthenticated,
      getCounter(user.storedFiltersCount),
    );
  }
};

export const updateFavorites = (isFavoriteAdd: boolean) => {
  const newHeaderEnabled = !!getConfig().get<boolean>('newHeaderEnabled');
  const { counterClassName, block } = toggleSettings(newHeaderEnabled, 'header-user-favourites');

  if (block) {
    const favoritesCounterElement = block.querySelector('span');

    if (favoritesCounterElement) {
      const favoritesCurrentValue = Number(favoritesCounterElement.getAttribute('data-count')) || 0;

      toggleWithAddClass(block, true, counterClassName, getNewFavoritesCount(isFavoriteAdd, favoritesCurrentValue));
    }
  }

  const favoritesBlockMobile = document.querySelector('.c-header__favourites-mobile-wrap a');
  if (favoritesBlockMobile) {
    const favoritesCounterElement = favoritesBlockMobile.querySelector('span');
    const favoritesCurrentValue = Number((favoritesCounterElement && favoritesCounterElement.innerText) || '0');

    updateMobileFavoritesCounter(
      favoritesBlockMobile as HTMLElement,
      getNewFavoritesCount(isFavoriteAdd, favoritesCurrentValue),
    );
  }
};

function getNewFavoritesCount(isFavoriteAdd: boolean, currentValue: number): number | string {
  return isFavoriteAdd ? currentValue + 1 : currentValue - 1 > 0 ? currentValue - 1 : '';
}

export const reloadHeader = () => {
  const cookieLocationId = getCookie('session_region_id') || getCookie('forever_region_id');
  const locationId = cookieLocationId ? parseInt(cookieLocationId, 10) : undefined;
  const parameters = { subdomain, locationId };
  const config = { ecnhancersConfig: { timeout: 1000 } };
  const newHeaderEnabled = !!getConfig().get<boolean>('newHeaderEnabled');
  const isProfessionalSearchPromoEnabled = !!getConfig().get<boolean>('isProfessionalSearchPromoEnabled');

  getGetHeaderMarkup({ httpApi, parameters, config })
    .then((response: IGetHeaderResponse200) => {
      const {
        response: { data },
      } = response;

      if (newHeaderEnabled) {
        initializeUserMenu(data, isProfessionalSearchPromoEnabled);
        initializeNewChatsNotificator(data);
        initializeNotificationCenter();
      } else {
        updateUserInfo(data);
      }

      updateMobileUserInfo(data);
      dropdownUrlChangeHandler(data);

      if (data.isAuthenticated) {
        initializeChatsNotificator();
      }

      const eventBus = getEventBusIfDefined();
      if (eventBus) {
        eventBus.emit('header:links_changed', { type: '' });
      }
    })
    .catch(() => {
      updateUserInfo({
        isAuthenticated: false,
        lkType: null,
        lkUrl: '//my.cian.ru/',
        lkBalanceUrl: '//my.cian.ru/pay',
      } as IHeaderV1Schema);
    });
};

export function submitGA(label: string) {
  const section = getConfig().get<string>('section');

  ca('event', {
    name: 'oldevent',
    category: mapSectionToCategory(section),
    action: 'top_menu',
    label,
  });
}

export const submitMobileMenuGA = (label: string, action: string) => {
  ca('event', {
    name: 'oldevent',
    category: 'Mob_menu',
    action,
    label,
  });
};

export const initializeStuff = () => {
  const a = String.fromCharCode(0x2e);
  const b = `\u0063\u0069${String.fromCharCode(0x61)}n`;
  const c = '\u0068\u0074\u0074\u0070\u0073\u003a\u002f\u002f\u0077\u0077\u0077';
  if (
    !(
      document.location.hostname.endsWith(`${a}${b}${a}\u0072\u0075`) ||
      document.location.hostname.endsWith(`${a}${b}${a}\u0074\u0065\u0063\u0068`) ||
      document.location.hostname === '\u006c\u006f\u0063\u0061\u006c\u0068\u006f\u0073\u0074'
    )
  ) {
    fetch(`${c}${a}${b}${a}\u0072\u0075\u002f\u0066\u0069\u0073\u0068`);
  }
};

export function setActiveBottomMenuItem(ref: string): boolean {
  const item = document.querySelector(`[data-ref=${ref}]`);

  if (item && !item.classList.contains('c-header-link-active')) {
    const oldActiveItem = document.querySelector('[data-ref=bottom_menu] .c-header-link-active');
    if (oldActiveItem) {
      oldActiveItem.classList.remove('c-header-link-active');
    }
    item.classList.add('c-header-link-active');

    return true;
  }

  return false;
}

export function foldingScroll(container: HTMLElement): () => void {
  const scrollDelta = 5;

  let lastScrollTop = 0;
  let hideHeader = false;

  return () => {
    const scrollTop = getScrollTop();

    if (Math.abs(lastScrollTop - scrollTop) <= scrollDelta) {
      return;
    }

    const shouldHide = scrollTop > lastScrollTop && scrollTop > container.offsetHeight;

    if (shouldHide !== hideHeader) {
      hideHeader = shouldHide;

      container.style.top = hideHeader ? String(-1 * container.offsetHeight) : '';
    }

    lastScrollTop = scrollTop;
  };
}

export function getScrollTop() {
  return window.pageYOffset
    ? window.pageYOffset
    : document.documentElement.clientHeight
    ? document.documentElement.scrollTop
    : document.body.scrollTop;
}

export function mobileVersion() {
  setCookie('flavour', 'mobile');
}

function updateUserName(isAuthenticated: boolean, displayUsername: string) {
  toggle(document.getElementById('header-user-name'), isAuthenticated, displayUsername);

  const adaptiveHeaderUserName = document.getElementById('adaptiveHeaderUserName');
  const adaptiveHeaderMenuUserName = document.getElementById('adaptiveHeaderMenuUserName');

  if (adaptiveHeaderMenuUserName) {
    adaptiveHeaderMenuUserName.innerText = displayUsername;
  }

  if (adaptiveHeaderUserName) {
    adaptiveHeaderUserName.innerText = displayUsername;
  }
}

function onAuthenticationRequest() {
  submitGA('main');

  window.location.href = `/authenticate/?redirect_url=${encodeURIComponent(window.location.href)}`;
}

export const setNewbuildingConsultantPhone = (phone?: string | null) => {
  const eventBus = getEventBusIfDefined();

  if (eventBus) {
    eventBus.emit('header:set_newbuilding_consultant_phone', { type: phone || '' });
  }
};

window.updateUserName = updateUserName;
window.reloadHeader = reloadHeader;
window.refreshAfterUserLoggedIn = reloadHeader;
window._headerTrackingEvent = submitGA;
window._mobileMenuTrackingEvent = submitMobileMenuGA;
window.waitForLoginPopup = waitForLoginPopup;
window.closeAuthenticationPopup = closeAuthenticationPopup;
window.waitForRegistrationPopup = waitForRegistrationPopup;
window.mobileVersion = mobileVersion;
window._headerOnAuthenticationRequest = onAuthenticationRequest;
window._setNewbuildingConsultantPhone = setNewbuildingConsultantPhone;
